/**
 * Maps application route patterns to guide topic keys.
 * Used by the help button to open the relevant guide section.
 *
 * Route patterns use the path relative to /t/{slug}.
 * More specific patterns are checked first via resolveHelpTopic().
 */
export const helpTopics: Record<string, string> = {
  // Dashboard
  '/dashboard': 'getting-started/overview',

  // Risks
  '/risks': 'risks/registers',
  '/risks/$riskId': 'risks/detail',
  '/risks/$riskId/controls': 'risks/controls-tab',
  '/risks/$riskId/actions': 'risks/actions-tab',
  '/risks/$riskId/events': 'risks/events-tab',
  '/risks/$riskId/history': 'risks/history',

  // Controls
  '/controls': 'controls/overview',
  '/controls/$controlId': 'controls/detail',
  '/controls/$controlId/assessments': 'controls/assessments',
  '/controls/$controlId/risks': 'controls/risks-tab',
  '/controls/$controlId/obligations': 'controls/obligations-tab',
  '/controls/$controlId/history': 'controls/history',

  // Obligations
  '/obligations': 'obligations/overview',
  '/obligations/$frameworkId': 'obligations/detail',

  // Actions
  '/actions': 'actions/overview',
  '/actions/$actionId': 'actions/detail',
  '/actions/$actionId/updates': 'actions/updates',
  '/actions/$actionId/evidence': 'actions/evidence',
  '/actions/$actionId/risks': 'actions/risks-tab',
  '/actions/$actionId/controls': 'actions/controls-tab',
  '/actions/$actionId/events': 'actions/events-tab',
  '/actions/$actionId/history': 'actions/history',

  // Events
  '/events': 'events/overview',
  '/events/$eventId': 'events/detail',
  '/events/$eventId/evidence': 'events/evidence',
  '/events/$eventId/risks': 'events/risks-tab',
  '/events/$eventId/actions': 'events/actions-tab',

  // Reporting
  '/reporting': 'reporting/risk-heatmap',

  // Admin
  '/admin': 'admin/overview',
  '/admin/users': 'admin/users',
  '/admin/users/$userId': 'admin/user-detail',
  '/admin/ownership-roles': 'admin/ownership-roles',
  '/admin/roles': 'admin/permission-roles',

  // Framework config
  '/framework': 'framework/overview',
  '/framework/taxonomy': 'framework/taxonomy',
  '/framework/scoring-grid': 'framework/scoring-grid',
  '/framework/likelihood': 'framework/likelihood',
  '/framework/impact-tables': 'framework/impact-tables',
  '/framework/appetite': 'framework/appetite',
  '/framework/assessment-thresholds': 'framework/assessment-thresholds',
  '/framework/evidence-tags': 'framework/evidence-tags',
  '/framework/categories/control': 'framework/categories',
  '/framework/categories/event': 'framework/categories',
  '/framework/categories/action': 'framework/categories',
  '/framework/categories/framework': 'framework/categories',

  // Settings
  '/settings': 'settings/branding',
  '/settings/branding': 'settings/branding',
}

/**
 * Resolve the guide topic key for a given route path.
 * Strips the /t/{slug} prefix before matching.
 * Replaces UUID segments with $param patterns for matching.
 * Returns the default topic if no match found.
 */
export function resolveHelpTopic(fullPath: string): string {
  // Strip /t/{slug} prefix
  const match = fullPath.match(/^\/t\/[^/]+(.*)$/)
  const routePath = match?.[1] ?? fullPath

  // Replace UUID segments with $param pattern for matching
  const uuidPattern = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

  // Try exact match first (with UUIDs replaced)
  const normalised = routePath.replace(uuidPattern, '/$id')

  // Try progressively shorter paths
  let path = normalised
  while (path) {
    // Check all topic entries for a match
    for (const [pattern, topic] of Object.entries(helpTopics)) {
      // Normalise pattern: replace $paramName with $id
      const normPattern = pattern.replace(/\$[a-zA-Z]+/g, '$id')
      if (normPattern === path) return topic
    }

    const lastSlash = path.lastIndexOf('/')
    if (lastSlash <= 0) break
    path = path.substring(0, lastSlash)
  }

  return 'getting-started/overview'
}

/**
 * Open the guide in a new window for the given topic.
 * Uses browser locale with fallback to 'en'.
 * Passes the tenant slug for branding consistency.
 * Reuses the same window (named 'riskapp-guide') on repeated calls.
 */
export function openGuide(topicKey?: string, tenantSlug?: string): void {
  const locale = navigator.language?.split('-')[0] || 'en'
  const key = topicKey || 'getting-started/overview'
  const params = tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : ''
  window.open(`/guide/${locale}/${key}${params}`, 'riskapp-guide')
}
